/**
 * Estilos comunes y componentes estilizados para el sistema de pruebas
 * 
 * Este archivo centraliza todos los estilos reutilizables del sistema:
 * - Paleta de colores consistente
 * - Componentes base estilizados
 * - Variantes temáticas
 * - Utilidades de layout
 * 
 * @author Ana Lizcano
 * @module CommonStyles
 */

import { styled } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";

/**
 * Paleta de colores principal del sistema
 * Define todos los colores utilizados en la aplicación
 */
const COLORS = {
  primary: '#667eea',    // Azul principal
  secondary: '#764ba2',  // Púrpura secundario
  success: '#4caf50',    // Verde para éxito
  error: '#f44336',      // Rojo para errores
  warning: '#ff9800',    // Naranja para advertencias
  info: '#2196f3',       // Azul para información
  light: '#f8f9fa',      // Gris claro
  dark: '#333333',       // Gris oscuro
  border: '#e0e0e0'      // Color de bordes
};

/**
 * Esquemas de colores para rankings/puntuaciones
 * Cada ranking tiene su combinación de colores específica
 */
const RANKINGS = {
  '4': { bg: '#e8f5e8', border: '#81c784', text: '#388e3c' }, // Verde suave - Excelente
  '3': { bg: '#e3f2fd', border: '#64b5f6', text: '#1976d2' }, // Azul suave - Bueno
  '2': { bg: '#fff8e1', border: '#ffb74d', text: '#f57c00' }, // Naranja suave - Regular
  '1': { bg: '#fce4ec', border: '#f06292', text: '#c2185b' }  // Coral suave - Bajo
};

/**
 * Componente base de tarjeta con múltiples variantes de estilo
 * @param {Object} theme - Tema de MUI
 * @param {string} variant - Variante de estilo: 'default', 'elevated', 'gradient', 'info'
 * @returns {Object} Estilos aplicados
 */
export const BaseCard = styled(Card)(({ theme, variant = 'default' }) => {
  const variants = {
    default: {
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    elevated: {
      backgroundColor: '#ffffff', 
      border: '2px solid #e0e0e0',
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
    },
    gradient: {
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      color: 'white',
      border: 'none',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
    },
    info: {
      backgroundColor: '#e3f2fd',
      border: '2px solid #2196f3',
      boxShadow: '0 4px 20px rgba(33, 150, 243, 0.2)'
    }
  };

  return {
    borderRadius: '12px',
    padding: theme.spacing(2),
    transition: 'all 0.3s ease',
    ...variants[variant],
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: variants[variant].boxShadow?.replace('0.1)', '0.2)') || '0 6px 20px rgba(0,0,0,0.2)'
    }
  };
});

/**
 * Contenedor flexible con propiedades de flexbox configurables
 * @param {string} direction - Dirección del flex: 'row', 'column'
 * @param {string} justify - Justificación del contenido
 * @param {string} align - Alineación de elementos
 * @param {number} gap - Espacio entre elementos
 * @returns {Object} Estilos de flexbox aplicados
 */
export const FlexContainer = styled(MDBox)(({ direction = 'row', justify = 'flex-start', align = 'stretch', gap = 1 }) => ({
  display: 'flex',
  flexDirection: direction,
  justifyContent: justify,
  alignItems: align,
  gap: `${gap * 8}px`
}));

/**
 * Contenedor de rejilla CSS Grid con columnas configurables
 * @param {number} columns - Número de columnas del grid
 * @param {number} gap - Espacio entre elementos del grid
 * @returns {Object} Estilos de CSS Grid aplicados
 */
export const GridContainer = styled(MDBox)(({ columns = 1, gap = 2 }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gap: `${gap * 8}px`,
  alignItems: 'center'
}));

/**
 * Área desplazable con scrollbar personalizado
 * Proporciona scroll vertical con diseño estilizado
 * @param {Object} theme - Tema de MUI
 * @returns {Object} Estilos de scroll personalizados
 */
export const ScrollableArea = styled(MDBox)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  paddingRight: theme.spacing(0.5),
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a1a1a1',
  },
}));

/**
 * Botón de acción personalizable con múltiples variantes y estados
 * @param {string} buttonVariant - Variante del botón: 'like', 'dislike', 'primary', 'secondary', 'default'
 * @param {boolean} selected - Estado de selección
 * @param {string} size - Tamaño: 'small', 'medium', 'large'
 * @returns {Object} Estilos del botón aplicados
 */
export const ActionButton = styled(MDButton)(({ 
  variant: buttonVariant = 'default', 
  selected = false,
  size = 'medium'
}) => {
  const variants = {
    like: COLORS.success,
    dislike: COLORS.error,
    primary: COLORS.primary,
    secondary: COLORS.secondary,
    default: COLORS.dark
  };

  const sizes = {
    small: { padding: '4px 16px', fontSize: '12px', minWidth: '80px' },
    medium: { padding: '8px 24px', fontSize: '14px', minWidth: '100px' },
    large: { padding: '12px 32px', fontSize: '16px', minWidth: '120px' }
  };

  const color = variants[buttonVariant];
  const sizeProps = sizes[size];

  return {
    borderRadius: '25px',
    fontWeight: 'bold',
    textTransform: 'none',
    border: `2px solid ${selected ? color : COLORS.border}`,
    backgroundColor: selected ? `${color}20` : '#ffffff',
    color: selected ? color : COLORS.dark,
    transition: 'all 0.3s ease',
    ...sizeProps,
    '&:hover': {
      backgroundColor: `${color}20`,
      color: color,
      borderColor: color,
      transform: 'translateY(-1px)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };
});

/**
 * Tarjeta especializada para mostrar rankings con colores temáticos
 * @param {Object} theme - Tema de MUI
 * @param {string} rankvalue - Valor del ranking ('1', '2', '3', '4')
 * @returns {Object} Estilos de la tarjeta con colores del ranking
 */
export const RankingCard = styled(BaseCard)(({ theme, rankvalue }) => {
  if (!rankvalue) return {};
  
  const colorScheme = RANKINGS[rankvalue];
  if (!colorScheme) return {};

  return {
    backgroundColor: colorScheme.bg,
    borderColor: colorScheme.border,
    color: colorScheme.text,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  };
});

/**
 * Caja de instrucciones con diferentes variantes visuales
 * @param {Object} theme - Tema de MUI
 * @param {string} variant - Variante: 'default', 'info', 'gradient'
 * @returns {Object} Estilos de la caja de instrucciones
 */
export const InstructionBox = styled(MDBox)(({ theme, variant = 'default' }) => {
  const variants = {
    default: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef'
    },
    info: {
      backgroundColor: '#e3f2fd',
      border: '1px solid #bbdefb'
    },
    gradient: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      border: '1px solid rgba(255,255,255,0.2)'
    }
  };

  return {
    padding: theme.spacing(1.5),
    borderRadius: '10px',
    textAlign: 'center',
    ...variants[variant]
  };
});

/**
 * Componente de visualización de temporizador con animaciones
 * @param {Object} theme - Tema de MUI
 * @param {string} timercolor - Color del temporizador
 * @returns {Object} Estilos del temporizador con animaciones
 */
export const TimerDisplay = styled(MDBox)(({ theme, timercolor = COLORS.primary }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: 'rgba(102, 126, 234, 0.1)',
  borderRadius: '16px',
  color: timercolor,
  fontWeight: 'bold',
  transition: 'color 0.3s ease',
  animation: timercolor === COLORS.error ? 'pulse 1s infinite' : 'none',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.7 },
    '100%': { opacity: 1 },
  },
}));

/**
 * Indicador de progreso visual con diferentes variantes
 * @param {Object} theme - Tema de MUI
 * @param {number} progress - Porcentaje de progreso (0-100)
 * @param {string} variant - Variante: 'default', 'success'
 * @returns {Object} Estilos del indicador de progreso
 */
export const ProgressIndicator = styled(MDBox)(({ theme, progress = 0, variant = 'default' }) => {
  const variants = {
    default: {
      backgroundColor: '#f5f5f5',
      color: COLORS.dark
    },
    success: {
      backgroundColor: '#e8f5e8',
      color: COLORS.success
    }
  };

  return {
    padding: theme.spacing(1.5),
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    textAlign: 'center',
    ...variants[variant]
  };
});

// Exportar constantes para uso en otros componentes
export { COLORS, RANKINGS };